import {mergeStyles} from 'components/FluentUICompat';
import React from 'react';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList';
import {InfinityParagraphId, ParagraphFilterOptions} from 'types';
import {useTranslation} from 'react-i18next';
import {
	renderArrayField,
	renderDateString,
	renderParagraph,
	renderPhase,
	renderStatus,
} from 'components/EntityList/ColumnRenderers';
import {CellWithEntityLink} from 'components/EntityList/CellWithEntityLink';

export const useAssignedParagraphsColumn = () => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'ParagraphsList',
	});

	const {t: tRegDoc} = useTranslation('features/reports', {
		keyPrefix: 'ReportsList',
	});
	const paragraphClassName = mergeStyles({
		paddingTop: '0px !important',
		paddingBottom: '0px !important',
		height: '100% !important',
	});

	const widthSTD120 = {minWidth: 120, maxWidth: 120};
	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	const filterArray = {
		...filterArraySimple,
		onRender: renderArrayField(),
	};
	const resizeMulti = {isResizable: true, isMultiline: true};
	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArray};
	const sortableDate = {sortable: true, filterType: 'Date'};
	const sortableDate120 = {
		...widthSTD120,
		...sortableDate,
		onRender: renderDateString(),
	};
	const phase = {
		...sortableDate120,
		filterType: 'Date',
		onRender: renderPhase(),
	};

	return [
		{
			key: 'regulationNumber',
			name: t('Regulation'),
			fieldName: 'regulationNumber',
			minWidth: 150,
			maxWidth: 150,
			...filterArraySimple,
			hiddenAlways: true,
		},
		{
			key: 'version',
			name: tRegDoc('Version'),
			fieldName: 'version',
			minWidth: 200,
			maxWidth: 300,
			...resizeMulti,
			onRender: item => (
				<CellWithEntityLink href={`/regulatoryDocuments/${item.parent.id}`}>
					<strong>{item.parent?.regulation?.regulationNumber ?? ''}</strong> |{' '}
					{item.parent?.name}
				</CellWithEntityLink>
			),
		},
		{
			key: 'position',
			name: t('Position'),
			fieldName: 'position',
			minWidth: 40,
			width: 40,
		},
		{
			key: 'paragraph',
			name: t('Paragraph'),
			fieldName: 'paragraph',
			minWidth: 500,
			className: paragraphClassName,
			isResizable: true,
			onRender: (item: any) => renderParagraph(item, t),
		},
		{
			key: 'markets',
			name: tRegDoc('Markets'),
			fieldName: 'markets',
			...filterArray150,
		},
		{
			key: 'status',
			name: tRegDoc('Status'),
			fieldName: 'status',
			...widthSTD120,
			sortable: true,
			filterType: 'Array',
			onRender: renderStatus('RegulatoryDocumentStatus'),
		},
		{
			key: 'mainKeywords',
			name: tRegDoc('MainKeywords'),
			fieldName: 'mainKeywords',
			...filterArray150,
		},
		{
			key: 'keywords',
			name: t('Keywords'),
			fieldName: 'keywords',
			...filterArray150,
		},
		{
			key: 'categories',
			name: t('Categories'),
			fieldName: 'categories',
			...filterArray150,
		},
		{
			key: 'dateNewTypes',
			name: t('DateNewTypes'),
			fieldName: 'dateNewTypes',
			...sortableDate120,
		},
		{
			key: 'dateNewRegistration',
			name: t('DateNewRegistration'),
			fieldName: 'dateNewRegistration',
			...sortableDate120,
		},
		{
			key: 'comprehensive',
			name: t('DateImplementation'),
			fieldName: 'comprehensive',
			...sortableDate120,
		},
		{
			key: 'modelYear',
			name: t('ModelYear'),
			fieldName: 'modelYear',
			...filterArray150,
			onRender: item => (item.modelYear ? t('MY') + item.modelYear : ''),
		},
		{
			key: 'phaseIn',
			name: t('PhaseIn'),
			fieldName: 'phaseIn',
			...phase,
		},
		{
			key: 'phaseOut',
			name: t('PhaseOut'),
			fieldName: 'phaseOut',
			...phase,
		},
	] as InfinityListColumn[];
};

export const useAssignedParagraphsColumnMetaData = () => {
	return [] as InfinityListColumnMetaData[];
};

export const useAssignedParagraphFilterOptions = (
	_paragraphFilterOptions: ParagraphFilterOptions,
) => {
	return [] as InfinityListColumnFilterOptions[];
};

export const convertParagraph2EntityId = (
	PreFilteredParagraphIds: InfinityParagraphId[],
) => PreFilteredParagraphIds.map(x => `${x.regDocId}_${x.paragraphId}`);

export const convertEntity2ParagraphId = (preFilteredIds: string[]) =>
	preFilteredIds.map(x => ({
		regDocId: x.split('_')[0],
		paragraphId: x.split('_')[1],
	}));
